/**
 * React
 */
import * as React from 'react';
import { Link } from '@fluentui/react';

/**
 * React form
 */
import { Field, reduxForm } from 'redux-form';

/**
 * Fabric UI
 */
import {
  PrimaryButton,
  DefaultButton
} from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { Dialog, DialogFooter } from '@fluentui/react/lib/Dialog';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import {
  ConstrainMode,
  DetailsList,
  SelectionMode
} from '@fluentui/react/lib/DetailsList';
import {FormEventHandler} from 'react';

interface IProps {
  communityId: string | number;
  brandName: string;
  isSubmitted: boolean;
  isProtected: boolean;
  initialValues: any;
  invitedMembers: any;
  constants: any;
  results: any;
  NormalPeoplePicker: React.ReactNode;

  requestFetchCommunity(communityId: string | number): void;
  requestSaveMembers(communityId: string | number, flag: boolean): void;
  endEditInviteMembers(communityId: string | number): void;
  toggleBulkInvite(event: React.MouseEvent<any>): void;

  initialize: any;
  handleSubmit(func: any): FormEventHandler;
  pristine: boolean;
  submitting: boolean;
}

const InviteMembersPickerComponent = (props: IProps) => {
  const {
    communityId,
    brandName,
    isSubmitted,
    isProtected,
    initialValues,
    invitedMembers,
    constants,
    results,
    NormalPeoplePicker,

    requestFetchCommunity,
    requestSaveMembers,
    endEditInviteMembers,
    toggleBulkInvite,

    initialize,
    handleSubmit,
    pristine,
    submitting,
  } = props;
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [selectedKey, setSelectedKey] = React.useState<string>('Community');

  const columns = [
    {
      key: 'emailAddress',
      name: 'Email',
      fieldName: 'emailAddress',
      minWidth: 250,
      maxWidth: 300,
      isResizable: true
    },
    {
      key: 'reason',
      name: 'Status',
      fieldName: 'reason',
      minWidth: 300,
      maxWidth: 400,
      isResizable: true,
      isMultiline: true
    }
  ];

  const _handleInvitationProcess = (e) => {
    toggleBulkInvite(e);
  };

  const onSubmit = () : any => {
    requestSaveMembers(communityId, false);
  };

  const onClose = (_event) => {
    /**
     * Check if form is submitted and if alert is showing
     */
    if (!isSubmitted && !showAlert) {
      setShowAlert(true);
    } else {
      endEditInviteMembers(communityId);
    }
  };

  const onDismissAlert = (_event) => {
    setShowAlert(false);
  };

  const handleLinkClick = (_item) => {
    setSelectedKey('Community');
  };

  const renderItemLink = (link, defaultRenderer) => {
    return (
      <span>
        {defaultRenderer(link)}
        <Icon iconName='ChevronRight' />
      </span>
    );
  };

  const onRenderItem = (item, _index, column) => {
    const fieldContent = item[column.fieldName];
    const key = column.key;

    switch (key) {
      case 'reason':
        return renderStatus(item);
      default:
        return fieldContent;
    }
  };

  const renderStatus = (item) => {
    let className = 'status-success';
    if (!item.success) {
      className = 'status-error';
    }
    return <span className={className}>{item.reason}</span>;
  };

  // React.useEffect( () => {
  //   initialize(initialValues);
  // }, [initialValues]);

  React.useEffect( () => {
    if ( !initialValues || (initialValues && initialValues.communityId !== communityId) ) {
      requestFetchCommunity(communityId);
    } else {
      initialize(initialValues);
    }
  }, [communityId, initialValues]);

  return (
    <React.Fragment>
      {initialValues && (
        <React.Fragment>
          <div className='formGrid formBasic'>
            <Pivot
              selectedKey={isSubmitted ? 'Summary' : selectedKey}
              onLinkClick={handleLinkClick}
            >
              <PivotItem
                headerText='Community'
                itemKey='Community'
                onRenderItemLink={renderItemLink}
              >
                {
                  !isProtected && (
                    <h1>Invite members and guests to {initialValues.displayName}</h1>
                  )
                }
                {
                  isProtected && (
                    <h1>Invite members to {initialValues.displayName} {isProtected === true}</h1>
                  )
                }

                <hr />

                {
                  isProtected && (
                    <p>Invite members to your community by typing their name and searching for existing {brandName} members. If they do not have an existing {brandName} account, you can type their email address and invite them to join {brandName}.</p>
                  )
                }
                {
                  isProtected && (
                    <p>You can invite up to 10 members using this method. If you would like to copy/paste a list of email addresses or invite more than 10 members, use the <Link onClick={_handleInvitationProcess}>use the bulk invitation process</Link></p>
                  )
                }

                {
                  isProtected && (
                    <p><strong>Participants must have</strong> (at minimum) an active baseline clearance to access the system (or an agreement/ arrangement in line with the PSPF and acceptable to the Department of Finance).</p>
                  )
                }

                {
                  isProtected && (
                    <p><strong>External members can only invite existing users</strong> to communities they own.</p>
                  )
                }

                {
                  !isProtected && (
                    <p>
                      Invite members and guests to your community by typing
                      their name and searching for existing {brandName} members. If
                      they do not have an existing {brandName} account, you can
                      type their email address and invite them to join {brandName}.
                    </p>
                  )
                }
                {
                  !isProtected && (
                    <p>
                      You can invite up to 10 members and guests using this
                      method. If you would like to copy/paste a list of email
                      addresses or invite more than 10 members and guests,{' '}
                      <Link onClick={_handleInvitationProcess}>
                        use the bulk invitation process
                      </Link>.
                    </p>
                  )
                }

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className='formFields'>
                    <div className='formField'>
                      <Field name='members' component={NormalPeoplePicker} />
                    </div>
                  </div>
                  <div className='formFooter'>
                    <PrimaryButton
                      text='Send invite'
                      type='submit'
                      disabled={pristine || submitting}
                    />
                  </div>
                  <MessageBar>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: constants.TEXT_INVITEMEMBERS_INFO
                      }}
                    />
                  </MessageBar>
                </form>
              </PivotItem>
              <PivotItem headerText='Summary' itemKey='Summary'>
                <React.Fragment>
                  <h1 className='heading-success'>
                    {constants.TEXT_INVITEMEMBERS_SUCCESSTITLE}
                  </h1>
                  <div
                    className='spacing-bottom'
                    dangerouslySetInnerHTML={{
                      __html: constants.TEXT_INVITEMEMBERS_SUCCESSTEXT
                    }}
                  />
                  <h2>
                    Invitation recipients
                    {invitedMembers && invitedMembers.length ? (<span>({invitedMembers.length})</span>) : ('')}
                  </h2>
                  <DetailsList
                    className='spacing-bottom'
                    columns={columns}
                    constrainMode={ConstrainMode.horizontalConstrained}
                    items={results}
                    selectionMode={SelectionMode.none}
                    onRenderItemColumn={onRenderItem}
                  />
                  <PrimaryButton
                    text='Close'
                    onClick={onClose}
                  />
                </React.Fragment>
              </PivotItem>
            </Pivot>
          </div>
          <Dialog
            dialogContentProps={{
              title: constants.TEXT_INVITEMEMBERS_ALERTTITLE,
              showCloseButton: true
            }}
            modalProps={{
              isBlocking: true,
              isDarkOverlay: true,
              className: 'govTeams-dialog'
            }}
            hidden={!showAlert}
            onDismiss={onDismissAlert}
          >
            <p>{constants.TEXT_INVITEMEMBERS_ALERTTEXT}</p>
            <DialogFooter>
              <DefaultButton onClick={onDismissAlert}>
                Cancel
              </DefaultButton>{' '}
              <PrimaryButton onClick={onClose}>Continue</PrimaryButton>
            </DialogFooter>
          </Dialog>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default reduxForm({
  form: 'InviteMembersForm',
})(InviteMembersPickerComponent);